import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  LayoutDashboard, Layers, LogOut, Sun, Moon, Menu, ShieldAlert, ArrowLeft,
} from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { useI18n } from "@/context/I18nContext";
import { LANGUAGES } from "@/i18n/translations";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const nav = [
  { to: "/admin", icon: LayoutDashboard, label: "nav_admin_dashboard" },
  { to: "/admin/plans", icon: Layers, label: "nav_admin_plans" },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const { lang, setLang, t } = useI18n();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const currentLang = LANGUAGES.find((l) => l.code === lang) || LANGUAGES[0];

  const SidebarContent = () => (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2 px-6 h-16 border-b border-border">
        <ShieldAlert className="h-5 w-5 text-[hsl(var(--warning))]" strokeWidth={2.2} />
        <div className="leading-none">
          <span className="font-display text-xl tracking-tight">YieldPilot</span>
          <p className="text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--warning))] mt-0.5">
            {t("super_admin_badge")}
          </p>
        </div>
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1">
        {nav.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.to === "/admin"}
            onClick={() => setOpen(false)}
            data-testid={`admin-nav-${n.to.split("/").pop()}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-colors ${
                isActive
                  ? "bg-secondary text-foreground font-medium"
                  : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
              }`
            }
          >
            <n.icon className="h-4 w-4" />
            {t(n.label)}
          </NavLink>
        ))}
      </nav>
      <div className="px-3 py-2">
        <Button
          variant="ghost"
          data-testid="back-to-app-btn"
          onClick={() => navigate("/dashboard")}
          className="w-full justify-start gap-3 text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          {t("back_to_app")}
        </Button>
      </div>
      <div className="px-3 py-4 border-t border-border">
        <div className="flex items-center gap-3 px-3 py-2">
          <div className="h-9 w-9 rounded-full bg-secondary flex items-center justify-center font-mono text-sm">
            {(user?.name || "U").slice(0, 1).toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium truncate">{user?.name}</p>
            <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
          </div>
        </div>
        <Button
          variant="ghost"
          data-testid="logout-btn"
          onClick={() => {
            logout();
            navigate("/login");
          }}
          className="w-full justify-start gap-3 text-muted-foreground hover:text-foreground mt-1"
        >
          <LogOut className="h-4 w-4" />
          {t("logout")}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background flex" data-testid="admin-layout">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 flex-col border-r border-[hsl(var(--warning))]/30 fixed inset-y-0">
        <SidebarContent />
      </aside>

      {/* Mobile sidebar */}
      {open && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-64 bg-background border-r border-[hsl(var(--warning))]/30">
            <SidebarContent />
          </div>
        </div>
      )}

      <div className="flex-1 lg:pl-64 flex flex-col min-w-0">
        <header className="sticky top-0 z-30 h-16 border-b border-border backdrop-blur-xl bg-background/70 flex items-center justify-between px-4 lg:px-8">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setOpen(true)}
            data-testid="mobile-menu-btn"
          >
            <Menu className="h-5 w-5" />
          </Button>
          <div className="flex-1" />
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" data-testid="lang-switcher" className="gap-2 rounded-sm">
                  <span>{currentLang.flag}</span>
                  <span className="hidden sm:inline text-xs uppercase">{currentLang.code}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-popover">
                {LANGUAGES.map((l) => (
                  <DropdownMenuItem
                    key={l.code}
                    data-testid={`lang-${l.code}`}
                    onClick={() => setLang(l.code)}
                    className="gap-2 cursor-pointer"
                  >
                    <span>{l.flag}</span> {l.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              variant="outline"
              size="icon"
              onClick={toggle}
              data-testid="theme-toggle"
              className="rounded-sm"
            >
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
          </div>
        </header>

        <motion.main
          key={window.location.pathname}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="flex-1 p-4 lg:p-8 max-w-[1400px] w-full"
        >
          <Outlet />
        </motion.main>
      </div>
    </div>
  );
}
